/**
 * DEV-1251 P3. Shared cadence formatter — extracted from RhythmTaskTray
 * so the Action Center tray can render the same anchor summary without
 * code duplication.
 *
 * Both functions take primitives (cadence type string, anchor JSON string)
 * and return null when the cadence has no anchor (e.g. "daily") or the
 * anchor JSON is malformed. Callers should treat null as "no anchor
 * text to show" and render nothing rather than a placeholder.
 *
 * Day-of-week index convention: 0 = Sunday, 6 = Saturday (matches the
 * platform-wide convention in materializer + admin authoring tray).
 * Month index convention: 1 = January, 12 = December (per migration 193
 * cadence_anchor column comments — `{"month": 1-12}`).
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DOWS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Resolve a cadence type to its human-readable label via i18n. Falls
 * back to the raw key when no translation exists (defensive — keeps
 * the tray rendering instead of showing "engagements.rhythmCadence.foo").
 */
export function cadenceLabel(
  cadenceType: string | null,
  t: (k: string) => string,
): string | null {
  if (!cadenceType) return null;
  const key = `engagements.rhythmCadence.${cadenceType}`;
  const localized = t(key);
  return localized === key ? cadenceType : localized;
}

/**
 * Parse `cadence_anchor` JSON and produce a human-readable summary line.
 * The anchor shape varies by `cadenceType` — see migration 193 column
 * comments for the per-type schema. Returns null when no anchor applies
 * (daily) or when the JSON doesn't match the expected shape.
 *
 * Cases handled:
 *   - weekly / biweekly: `{"day_of_week": 0-6}` → "Mon"
 *   - monthly_dom: `{"day_of_month": 1-31}` → "Day 12 of month"
 *   - quarterly: `{"month_of_quarter": 1-3, "day_of_month": 1-31}`
 *   - semiannual: `{"months": [m1, m2], "day_of_month": 1-31}`
 *   - annual: `{"month": 1-12, "day_of_month": 1-31}` → "May 15"
 *   - everything else: null
 */
export function cadenceAnchorSummary(
  cadenceType: string | null,
  anchorJson: string | null,
): string | null {
  if (!cadenceType || !anchorJson) return null;
  let anchor: Record<string, unknown>;
  try {
    anchor = JSON.parse(anchorJson);
  } catch {
    return null;
  }

  switch (cadenceType) {
    case "weekly":
    case "biweekly": {
      const dow = Number(anchor.day_of_week);
      if (Number.isInteger(dow) && dow >= 0 && dow <= 6) return DOWS[dow];
      return null;
    }
    case "monthly_dom": {
      const day = Number(anchor.day_of_month);
      if (Number.isInteger(day)) return `Day ${day} of month`;
      return null;
    }
    case "quarterly": {
      const moq = Number(anchor.month_of_quarter);
      const day = Number(anchor.day_of_month);
      if (Number.isInteger(moq) && Number.isInteger(day))
        return `Month ${moq} of quarter, day ${day}`;
      return null;
    }
    case "semiannual": {
      const monthsArr = anchor.months;
      const day = Number(anchor.day_of_month);
      if (Array.isArray(monthsArr) && Number.isInteger(day)) {
        const names = monthsArr
          .map((m) =>
            Number.isInteger(m) && (m as number) >= 1 && (m as number) <= 12
              ? MONTHS[(m as number) - 1]
              : null,
          )
          .filter(Boolean)
          .join(", ");
        if (names) return `${names} · day ${day}`;
      }
      return null;
    }
    case "annual": {
      const month = Number(anchor.month);
      const day = Number(anchor.day_of_month);
      if (Number.isInteger(month) && month >= 1 && month <= 12 && Number.isInteger(day))
        return `${MONTHS[month - 1]} ${day}`;
      return null;
    }
    default:
      return null;
  }
}
